import { useEffect, useRef, useState } from "react";
import AudiImage from "@/assets/ville-kaisla-HNCSCpWrVJA-unsplash.jpg";

const About: React.FC = () => {
	const sectionRef = useRef<HTMLElement | null>(null);
	const [isVisible, setIsVisible] = useState<boolean>(false);

	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry?.isIntersecting) {
					setIsVisible(true);
					observer.unobserve(entry.target);
				}
			},
			{ threshold: 0.2 },
		);

		if (sectionRef.current) {
			observer.observe(sectionRef.current);
		}

		return () => observer.disconnect();
	}, []);

	return (
		<section
			id="about"
			ref={sectionRef}
			className="relative z-10 bg-black text-white py-32 overflow-hidden"
		>
			<div className="max-w-7xl mx-auto px-6 lg:px-8">
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
					{/* Image with Decorative Frame */}
					<div
						className={`relative transition-all duration-1000 ${
							isVisible
								? "opacity-100 translate-x-0"
								: "opacity-0 -translate-x-20"
						}`}
					>
						<div className="absolute -top-6 -left-6 w-32 h-32 border-t-2 border-l-2 border-[#cfc297]/30 z-0" />
						<div className="absolute -bottom-6 -right-6 w-32 h-32 border-b-2 border-r-2 border-[#cfc297]/30 z-0" />

						<div className="relative z-10 aspect-[3/4] overflow-hidden rounded-sm bg-zinc-900 group">
							<img
								src={AudiImage}
								alt="Professionelle Fahrzeugaufbereitung - Audi"
								className="w-full h-full object-cover grayscale-[30%] group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105"
								loading="lazy"
							/>
							<div className="absolute inset-0 bg-gradient-to-tr from-black/40 to-transparent" />
						</div>

						{/* Floating Brand Mark - Redesigned as a Badge/Hexagon */}
						<div 
							className="absolute -bottom-10 -left-10 w-44 h-44 bg-[#cfc297] text-black flex flex-col items-center justify-center p-4 text-center -rotate-6 shadow-2xl z-20 group/badge"
							style={{ 
								clipPath: "polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)"
							}}
						>
							<div className="border-2 border-black/10 absolute inset-2 pointer-events-none" 
								style={{ clipPath: "polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)" }} 
							/>
							<span className="text-[10px] uppercase tracking-[0.2em] font-black leading-tight mb-1 opacity-70">
								Since
							</span>
							<span className="text-4xl font-black italic tracking-tighter leading-none mb-1 drop-shadow-sm">
								2014
							</span>
							<div className="w-12 h-[2px] bg-black my-1" />
							<span className="text-[9px] uppercase tracking-[0.15em] font-black max-w-[80px]">
								The Detailing Era
							</span>
						</div>
					</div>

					{/* Content */}
					<div
						className={`flex flex-col transition-all duration-1000 delay-300 ${
							isVisible
								? "opacity-100 translate-x-0"
								: "opacity-0 translate-x-20"
						}`}
					>
						<p className="text-[#cfc297] text-xs uppercase tracking-[0.5em] font-black mb-6">
							Uncompromising Quality
						</p>

						<h2 className="text-5xl md:text-6xl lg:text-7xl font-black mb-10 leading-[0.9] uppercase tracking-tighter">
							Leidenschaft <br />
							<span className="text-[#cfc297]">●</span> Perfektion
						</h2>

						<div className="space-y-8 text-white/60 font-light leading-relaxed uppercase tracking-widest text-[11px] md:text-xs">
							<p className="border-l-2 border-[#cfc297] pl-6 py-2">
								In Sachen{" "}
								<strong className="text-white font-black">
									Fahrzeugaufbereitung
								</strong>{" "}
								sind wir die Instanz im Ruhrgebiet.
							</p>

							<p className="pl-6">
								Profitiere von unserer jahrelangen Erfahrung. Egal ob Klassiker,
								Sportwagen oder Alltagsfahrzeug – mit einem exklusiven,
								individuell abgestimmten Konzept bringen wir jedes Fahrzeug auf
								High-End Niveau.
							</p>

							<p className="pl-6">
								Unser Team arbeitet ausschließlich mit selektierten{" "}
								<span className="text-[#cfc297]">Premium-Produkten</span>. Vom
								chemischen Tiefensäubern bis zur mehrstufigen High-Gloss
								Lackkorrektur – wir setzen Maßstäbe.
							</p>
						</div>

						{/* Stats - Redesigned */}
						<div className="grid grid-cols-3 gap-8 mt-16 pt-12 border-t border-white/5">
							<div className="flex flex-col">
								<span className="text-4xl font-black text-white italic tracking-tighter mb-1">
									10+
								</span>
								<span className="text-[#cfc297] text-[10px] uppercase tracking-widest font-black">
									Jahre
								</span>
							</div>
							<div className="flex flex-col border-x border-white/5 px-8">
								<span className="text-4xl font-black text-white italic tracking-tighter mb-1">
									1k+
								</span>
								<span className="text-[#cfc297] text-[10px] uppercase tracking-widest font-black">
									Cars
								</span>
							</div>
							<div className="flex flex-col text-right">
								<span className="text-4xl font-black text-white italic tracking-tighter mb-1">
									100%
								</span>
								<span className="text-[#cfc297] text-[10px] uppercase tracking-widest font-black">
									Glanz
								</span>
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
};

interface StatProps {
	readonly value: string;
	readonly label: string;
}

const _Stat: React.FC<StatProps> = ({ value, label }) => (
	<div className="text-center">
		<div className="text-3xl md:text-4xl font-bold text-[#cfc297]">{value}</div>
		<div className="text-sm text-white/70 mt-1">{label}</div>
	</div>
);

export default About;
